// Ranking for the global search palette. Everything it searches is already in
// the browser (the CPMS fleet cache, the nav table) or pre-filtered upstream
// (ODA), so matching is a plain in-memory scan — no index, no fuzzy library.

/** Comparison key: case-, accent- and whitespace-insensitive. */
export function normalize(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();
}

// Weights, strongest first: the whole field is the query, the field starts with
// it, a word inside it starts with it, or it appears anywhere.
const EXACT = 100;
const PREFIX = 70;
const WORD = 50;
const CONTAINS = 25;

/** Per-position penalty — the first field is the most identifying one. */
const FIELD_PENALTY = 6;

const WORD_BOUNDARY = /[\s\-_/.,:]/;

/**
 * Score `fields` against `query`, best field wins. Every whitespace-separated
 * token of the query must hit some field (so "amsterdam 04" only matches a
 * station whose id and location both contribute); a single miss returns `null`,
 * meaning "not a result".
 *
 * Fields are passed most-identifying first — a hit on a station's own id
 * outranks the same hit inside its location name.
 */
export function matchScore(query: string, fields: (string | null | undefined)[]): number | null {
  const tokens = normalize(query).split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return null;

  const haystacks = fields
    .filter((field): field is string => Boolean(field))
    .map((field) => normalize(field));
  if (haystacks.length === 0) return null;

  let total = 0;
  for (const token of tokens) {
    let best = 0;
    haystacks.forEach((hay, index) => {
      const at = hay.indexOf(token);
      if (at < 0) return;
      const base =
        hay === token
          ? EXACT
          : at === 0
            ? PREFIX
            : WORD_BOUNDARY.test(hay[at - 1] ?? '')
              ? WORD
              : CONTAINS;
      best = Math.max(best, base - index * FIELD_PENALTY);
    });
    if (best === 0) return null;
    total += best;
  }

  return total / tokens.length;
}
